import { Theme } from "@mui/material";
import { $getRoot, $isDecoratorNode, $isTextNode, $isElementNode, ElementNode } from "lexical";

/**
 * Checks if the editor content is empty or contains only whitespace
 * @returns {boolean} True if the editor is empty or contains only whitespace
 */
export function $isEmpty() {
  const root = $getRoot();
  const firstChild = root.getFirstChild();

  if (!firstChild) {
    return true;
  }

  return $isWhitespace(root);
}

/**
 * Recursively checks if a node contains only whitespace
 * @param {ElementNode} node The node to check
 * @returns {boolean} True if the node contains only whitespace
 */
export function $isWhitespace(node: ElementNode): boolean {
  for (const child of node.getChildren()) {
    if (
      ($isElementNode(child) && !$isWhitespace(child)) ||
      ($isTextNode(child) && child.getTextContent().trim() !== "") ||
      $isDecoratorNode(child) // decorator nodes are arbitrary
    ) {
      return false;
    }
  }
  return true;
}

/**
 * Checks if a string value represents rich text content by checking for the "LXCL:" prefix
 * @param {string} value The string to check
 * @returns {boolean} True if the string represents rich text content
 */

export function isRichText(value: string) {
  return value.startsWith("LXCL:");
}

/**
 * Serializes HTML content by prefixing it with "LXCL:" to mark it as rich text (to differentiate from plain text already in the database)
 * @param {string} outputHtml The HTML string to serialize
 * @returns {string} The serialized HTML string prefixed with "LXCL:"
 */
export function serialize(outputHtml: string) {
  return `LXCL:${outputHtml}`;
}

/**
 * Deserializes input content, determining if it's HTML or plain text
 * @param {string | null | undefined} inputHtmlOrString The input content to deserialize
 * @returns {{ isLexicalRichText: boolean, value: string }} Object containing the deserialized content and type flag
 */
export function deserialize(inputHtmlOrString: string | null | undefined) {
  if (!inputHtmlOrString) {
    return {
      isLexicalRichText: false,
      value: "",
    };
  }

  if (isRichText(inputHtmlOrString)) {
    return {
      isLexicalRichText: true,
      value: inputHtmlOrString.replace(/^LXCL:/, ""),
    };
  }

  return {
    isLexicalRichText: false,
    value: inputHtmlOrString,
  };
}

export const getRichTextInputTextStyles = (theme: Theme) => ({
  ["& p"]: {
    margin: 0,
  },
  ["& .lxcl-bold"]: {
    fontWeight: theme.typography.fontWeightBold,
  },
  ["& .lxcl-italic"]: {
    fontStyle: "italic",
  },
  ["& .lxcl-underline"]: {
    textDecoration: "underline",
  },
});

export const getRichTextInputLinkStyles = (linkColor: string) => ({
  ["& .lxcl-link"]: {
    color: `${linkColor} !important`,
  },
});
